/**
 * Visitor Kiosk API client
 * 
 * Completely separate from the DAM JWT authentication system.
 * Uses a dedicated kiosk Bearer token stored in localStorage.
 */

import axios, { AxiosInstance } from 'axios';

const KIOSK_TOKEN_KEY = 'kioskVisitorToken';

// API base URL - use same as main app or configure separately
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Create a separate axios instance for kiosk (no JWT interceptors)
const kioskAxios: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth header to all requests
kioskAxios.interceptors.request.use((config) => {
  const token = getKioskToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// =============================================================================
// Token management
// =============================================================================

export function getKioskToken(): string | null {
  return localStorage.getItem(KIOSK_TOKEN_KEY);
}

export function setKioskToken(token: string): void {
  localStorage.setItem(KIOSK_TOKEN_KEY, token);
}

export function clearKioskToken(): void {
  localStorage.removeItem(KIOSK_TOKEN_KEY);
}

export function hasKioskToken(): boolean {
  const token = getKioskToken();
  return token !== null && token.length > 0;
}

// =============================================================================
// Types
// =============================================================================

export type VisitReason = 'Möte' | 'Leverans' | 'Intervju' | 'Underhåll' | 'Övrigt';

export const VISIT_REASONS: VisitReason[] = ['Möte', 'Leverans', 'Intervju', 'Underhåll', 'Övrigt'];

export interface CheckInRequest {
  fullName: string;
  company?: string;
  hostName?: string;
  visitReason: VisitReason;
  otherReasonText?: string;
  kioskId?: string;
}

export interface CheckInResponse {
  visitId: string;
  status: string;
  checkInAt: string;
}

export interface CheckOutRequest {
  visitId: string;
}

export interface CheckOutResponse {
  visitId: string;
  status: string;
  checkOutAt: string;
}

export interface CheckOutAllResponse {
  checkedOutCount: number;
  message: string;
}

export interface Visitor {
  _id: string;
  fullName: string;
  company?: string;
  hostName?: string;
  visitReason: VisitReason;
  checkInAt: string;
  kioskId?: string;
}

export interface ActiveVisitorsResponse {
  count: number;
  visitors: Visitor[];
}

export interface SearchVisitorsResponse {
  count: number;
  visitors: Visitor[];
}

export interface StatsResponse {
  inBuildingCount: number;
}

export interface PinVerifyResponse {
  success: boolean;
  message?: string;
}

export interface PinChangeResponse {
  success: boolean;
  message?: string;
}

export interface ApiError {
  message: string;
  errors?: Array<{ field: string; message: string }>;
}

// =============================================================================
// API functions
// =============================================================================

/**
 * Check in a visitor
 */
export async function checkIn(data: CheckInRequest): Promise<CheckInResponse> {
  const response = await kioskAxios.post<CheckInResponse>('/api/visitor/check-in', data);
  return response.data;
}

/**
 * Check out a visitor
 */
export async function checkOut(visitId: string): Promise<CheckOutResponse> {
  const response = await kioskAxios.post<CheckOutResponse>('/api/visitor/check-out', { visitId });
  return response.data;
}

/**
 * Check out all active visitors
 */
export async function checkOutAll(): Promise<CheckOutAllResponse> {
  const response = await kioskAxios.post<CheckOutAllResponse>('/api/visitor/check-out-all');
  return response.data;
}

/**
 * Get list of active visitors (in building)
 */
export async function getActiveVisitors(limit: number = 100): Promise<ActiveVisitorsResponse> {
  const response = await kioskAxios.get<ActiveVisitorsResponse>('/api/visitor/active', {
    params: { limit },
  });
  return response.data;
}

/**
 * Search for visitors (for check-out)
 */
export async function searchVisitors(query: string, limit: number = 20): Promise<SearchVisitorsResponse> {
  const response = await kioskAxios.get<SearchVisitorsResponse>('/api/visitor/search', {
    params: { q: query, limit },
  });
  return response.data;
}

/**
 * Get visitor statistics
 */
export async function getStats(): Promise<StatsResponse> {
  const response = await kioskAxios.get<StatsResponse>('/api/visitor/stats');
  return response.data;
}

/**
 * Test if the kiosk token is valid
 */
export async function testConnection(): Promise<boolean> {
  try {
    await getStats();
    return true;
  } catch {
    return false;
  }
}

/**
 * Verify PIN for overview access
 */
export async function verifyPin(pin: string): Promise<PinVerifyResponse> {
  const response = await kioskAxios.post<PinVerifyResponse>('/api/visitor/pin/verify', { pin });
  return response.data;
}

/**
 * Change PIN (requires old PIN)
 */
export async function changePin(oldPin: string, newPin: string): Promise<PinChangeResponse> {
  const response = await kioskAxios.post<PinChangeResponse>('/api/visitor/pin/change', { oldPin, newPin });
  return response.data;
}

/**
 * Extract error message from API error response
 */
export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as ApiError | undefined;
    if (data?.message) {
      return data.message;
    }
    if (error.response?.status === 401) {
      return 'Ogiltig eller saknad kiosk-token.';
    }
    if (error.response?.status === 403) {
      return 'Åtkomst nekad. Kontrollera kiosk-token.';
    }
    if (error.response?.status === 429) {
      return 'För många förfrågningar. Vänta en stund.';
    }
  }
  return 'Ett oväntat fel uppstod. Försök igen.';
}
